import type React from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'

import type { ButtonProps } from '@components/ui/Buttons'
import { IconButton } from '@components/ui/Buttons'
import { CopyIcon } from '@components/ui/svgIcons'
import { Tooltip } from '@components/ui/Tooltips'

import { CopyToClipboard } from './CopyToClipboard'
import { CopyFeedbackIcon, copyFeedbackMessages, preventClick, useCopyFeedback } from './elements'

const IconWrapperSpan = styled.span`
  display: inherit;
  align-items: inherit;
`

interface CopyAndPasteIconButtonProps extends Omit<ButtonProps, 'children' | 'title'> {
  icon?: React.ReactNode
  text?: Nullable<string>
  title?: React.ReactNode
}

/**
 * A button that copies the given text to the clipboard and shows a feedback icon for a short duration.
 * NOTE: Does not render anything if the `text` prop is not provided. Can cause layout shift.
 */
export function CopyAndPasteIconButton({
  icon = <CopyIcon size={16} />,
  onClick = preventClick,
  text,
  title,
  ...rest
}: CopyAndPasteIconButtonProps) {
  const { onCopy, status } = useCopyFeedback()
  const { formatMessage } = useIntl()

  if (!text) {
    return null
  }

  return (
    <CopyToClipboard text={text} onCopy={onCopy}>
      <IconButton onClick={onClick} {...rest}>
        <Tooltip title={title ?? formatMessage(copyFeedbackMessages.copy)}>
          <IconWrapperSpan>
            <CopyFeedbackIcon icon={icon} status={status} />
          </IconWrapperSpan>
        </Tooltip>
      </IconButton>
    </CopyToClipboard>
  )
}
